/**
 * EstimateDetailModal (Phase D1.1-B · Feb 2026)
 * ---------------------------------------------
 * Read-only view of an Estimate with action bar:
 *   • Edit          (draft/sent only)
 *   • Mark as Sent
 *   • Approve
 *   • Cancel
 *   • Set Follow-up (date + notes)
 *   • Download PDF
 *   • Email (mailto)
 *   • WhatsApp (wa.me)
 *   • Duplicate as new Estimate
 *
 * State transitions are guarded on the backend; this UI just requests them.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { groupItemsByProduct } from "@/lib/groupItems";
import { FilePreviewModal, isPreviewableFile } from "@/components/FilePreviewModal";
import { PartnerPointBlock } from "@/components/PartnerPointBlock";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const STATUS_LABEL_KEYS = {
  draft: "est_status_draft",
  sent: "est_status_sent",
  approved: "est_status_approved",
  expired: "est_status_expired",
  cancelled: "est_status_cancelled",
};
const STATUS_TONE = {
  draft: "bg-slate-200 text-slate-900",
  sent: "bg-amber-200 text-amber-900",
  approved: "bg-emerald-200 text-emerald-900",
  expired: "bg-neutral-300 text-neutral-800",
  cancelled: "bg-red-200 text-red-900",
};

export default function EstimateDetailModal({
  estimateId,
  onClose,
  onChanged,
  onEdit,
  onOpenInvoice,
  settings,
}) {
  const { isAdmin, user, can } = useAuth();
  const { t } = useI18n();
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followDate, setFollowDate] = useState("");
  const [followNotes, setFollowNotes] = useState("");
  const [converting, setConverting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/commercial/estimates/${estimateId}`,
        { withCredentials: true }
      );
      setEst(data);
      setFollowDate(data.follow_up_date || "");
      setFollowNotes(data.follow_up_notes || "");
    } catch {
      toast.error(t("est_detail_load_err"));
      onClose();
    } finally {
      setLoading(false);
    }
    // Deps: sólo estimateId. `onClose`/`t` se usan únicamente dentro del
    // callback (onClose = setter estable; t sólo para el toast de error).
    // Incluirlos hacía que cada re-render del padre (poller de alertas 60s)
    // recreara `load` y recargara el modal, borrando lo escrito en la
    // sección de archivos. P0 fix.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId]);

  useEffect(() => {
    load();
  }, [load]);

  const doAction = async (action) => {
    try {
      const { data } = await axios.post(
        `${API}/commercial/estimates/${estimateId}/${action}`,
        {},
        { withCredentials: true }
      );
      setEst(data);
      onChanged && onChanged();
      const labels = {
        send: t("est_detail_toast_send"),
        approve: t("est_detail_toast_approve"),
        cancel: t("est_detail_toast_cancel"),
        duplicate: t("est_detail_toast_duplicate", { n: data.number }),
      };
      toast.success(labels[action] || t("generic_ok"));
      if (action === "duplicate") {
        onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("est_detail_action_err", { action }));
    }
  };

  const saveFollowUp = async () => {
    try {
      const { data } = await axios.post(
        `${API}/commercial/estimates/${estimateId}/follow-up`,
        { follow_up_date: followDate, follow_up_notes: followNotes },
        { withCredentials: true }
      );
      setEst(data);
      setShowFollowUp(false);
      onChanged && onChanged();
      toast.success(t("est_detail_followup_ok"));
    } catch {
      toast.error(t("est_detail_followup_err"));
    }
  };

  const logAction = async (action, meta = {}) => {
    try {
      await axios.post(
        `${API}/commercial/estimates/${estimateId}/log-action`,
        { action, meta },
        { withCredentials: true }
      );
    } catch {
      /* non-fatal */
    }
  };

  const downloadPdf = async () => {
    // Feb 2026 — ventana síncrona en el clic (anti popup-blocker).
    const w = window.open("", "_blank");
    try {
      const res = await axios.get(
        `${API}/commercial/estimates/${estimateId}/pdf`,
        { responseType: "blob", withCredentials: true }
      );
      const url = URL.createObjectURL(res.data);
      if (w) w.location = url;
      else window.open(url, "_blank");
      logAction("pdf_generated");
    } catch {
      if (w) w.close();
      toast.error(t("est_detail_pdf_err"));
    }
  };

  const printPdf = async () => {
    // Feb 2026 — ventana síncrona en el clic (anti popup-blocker).
    const win = window.open("", "_blank");
    try {
      const res = await axios.get(
        `${API}/commercial/estimates/${estimateId}/pdf`,
        { responseType: "blob", withCredentials: true }
      );
      const url = URL.createObjectURL(res.data);
      if (win) {
        win.addEventListener("load", () => {
          try {
            win.focus();
            win.print();
          } catch {
            /* ignore */
          }
        });
        win.location = url;
      } else {
        window.open(url, "_blank");
      }
      logAction("printed");
    } catch {
      if (win) win.close();
      toast.error(t("est_detail_print_err"));
    }
  };

  const convertToInvoice = async () => {
    if (!window.confirm(t("est_detail_convert_confirm"))) return;
    setConverting(true);
    try {
      const { data } = await axios.post(
        `${API}/commercial/estimates/${estimateId}/convert-to-invoice`,
        {},
        { withCredentials: true }
      );
      toast.success(t("est_detail_invoice_created", { n: data.number }));
      // If the customer has Store Credit available, prompt to apply it to the new invoice.
      const availableSc = Number(data.available_store_credit || 0);
      if (availableSc > 0) {
        const applicable = Math.min(availableSc, Number(data.balance_due || 0));
        if (
          applicable > 0 &&
          window.confirm(
            t("est_detail_sc_prompt", {
              avail: availableSc.toFixed(2),
              apply: applicable.toFixed(2),
              n: data.number,
            })
          )
        ) {
          try {
            await axios.post(
              `${API}/commercial/invoices/${data.id}/payments`,
              {
                amount: applicable,
                payment_method: "Store Credit",
                notes: t("est_detail_sc_notes"),
                date: new Date().toISOString().slice(0, 10),
              },
              { withCredentials: true }
            );
            toast.success(t("est_detail_sc_applied", { amt: applicable.toFixed(2) }));
          } catch (e) {
            toast.error(e?.response?.data?.detail || t("est_detail_sc_err"));
          }
        }
      }
      onChanged && onChanged();
      if (onOpenInvoice) {
        onClose();
        onOpenInvoice(data.id);
      } else {
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("est_detail_convert_err"));
    } finally {
      setConverting(false);
    }
  };

  const softDelete = async () => {
    if (!window.confirm(t("est_detail_softdelete_confirm"))) return;
    try {
      await axios.delete(`${API}/commercial/estimates/${estimateId}`, {
        withCredentials: true,
      });
      toast.success(t("est_detail_softdelete_ok"));
      onChanged && onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("est_detail_softdelete_err"));
    }
  };

  const emailHref = est?.customer_email
    ? `mailto:${est.customer_email}?subject=${encodeURIComponent(
        `Cotización ${est.number} — ${settings?.company_name || "Rawen's"}`
      )}&body=${encodeURIComponent(
        `Hola ${est.contact_person || ""},\n\nAdjunto la cotización ${est.number}.\n\nGracias.`
      )}`
    : null;

  const waDigits = String(est?.customer_phone || "").replace(/\D/g, "");

  // Feb 2026 — link público con token secreto (share_token). Ventana abierta
  // de forma síncrona para evitar el bloqueador de popups.
  const openWhatsApp = async () => {
    if (!waDigits) return;
    const w = window.open("", "_blank");
    try {
      const { data } = await axios.post(
        `${API}/commercial/estimates/${est.id}/share-token`,
        {},
        { withCredentials: true }
      );
      const url = `${window.location.origin}/?e=${data.token}`;
      const tpl =
        settings?.whatsapp_template ||
        "Hola {contact}, le compartimos la cotización {number} de {company}. {url}";
      let msg = tpl
        .replaceAll("{contact}", est.contact_person || "")
        .replaceAll("{company}", settings?.company_name || "")
        .replaceAll("{number}", est.number || "")
        .replaceAll("{url}", url);
      if (!msg.includes(url)) msg = `${msg}\n${url}`;
      const wa = `https://wa.me/${waDigits}?text=${encodeURIComponent(msg)}`;
      if (w) w.location = wa;
      else window.open(wa, "_blank");
      logAction("whatsapped");
    } catch {
      if (w) w.close();
      toast.error("No se pudo generar el enlace para compartir");
    }
  };

  const status = est?.status;
  const isInvoiced = !!est?.invoice_id;
  const editable = (status === "draft" || status === "sent") && !isInvoiced;
  const showCosts = can && can("costs", "view");

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-auto"
      onClick={onClose}
    >
      <div
        data-testid="estimate-detail-modal"
        className="bg-white w-full max-w-4xl border border-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {loading || !est ? (
          <div className="p-10 text-center font-mono-tech text-sm text-[var(--text-secondary)]">
            {t("loading_ellipsis")}
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="p-5 border-b border-black flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                {settings?.company_logo_url && (
                  <img
                    src={settings.company_logo_url}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    className="w-14 h-14 object-contain border border-[var(--border)] bg-white p-1"
                  />
                )}
                <div>
                  <div className="label-overline">{t("est_detail_label")}</div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">
                      {est.number}
                    </h2>
                    <span
                      data-testid="estimate-detail-status"
                      className={`font-mono-tech text-sm font-bold tracking-wider px-2 py-1 ${
                        STATUS_TONE[status] || "bg-neutral-100"
                      }`}
                    >
                      {STATUS_LABEL_KEYS[status] ? t(STATUS_LABEL_KEYS[status]) : status}
                    </span>
                    {isInvoiced && (
                      <span
                        data-testid="estimate-detail-invoiced-badge"
                        className="font-mono-tech text-sm font-bold tracking-wider px-2 py-1 bg-emerald-600 text-white"
                      >
                        {t("est_detail_invoiced_badge", { n: est.invoice_number })}
                      </span>
                    )}
                  </div>
                  <p className="text-base text-[var(--text-secondary)] mt-1">
                    {est.company} · {est.contact_person} · {est.customer_phone}
                  </p>
                  {est.partner_point && (
                    <div className="mt-3 max-w-md">
                      <PartnerPointBlock pp={est.partner_point} compact />
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="estimate-detail-close"
                className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
              >
                {t("close")}
              </button>
            </header>

            {/* Action bar */}
            <div className="p-4 border-b border-[var(--border)] flex flex-wrap gap-2">
              {editable && (
                <ActionBtn
                  testid="estimate-detail-edit"
                  onClick={() => onEdit(est)}
                >
                  {t("action_edit_icon")}
                </ActionBtn>
              )}
              {status === "draft" && !isInvoiced && (
                <ActionBtn
                  testid="estimate-detail-send"
                  primary
                  onClick={() => doAction("send")}
                >
                  {t("est_detail_send_btn")}
                </ActionBtn>
              )}
              {(status === "draft" || status === "sent") && !isInvoiced && (
                <ActionBtn
                  testid="estimate-detail-approve"
                  primary
                  onClick={() => doAction("approve")}
                >
                  {t("action_approve_icon")}
                </ActionBtn>
              )}
              {status === "approved" && !isInvoiced && (
                <ActionBtn
                  testid="estimate-detail-convert"
                  primary
                  onClick={convertToInvoice}
                >
                  {converting ? t("est_detail_converting_btn") : t("est_detail_convert_btn")}
                </ActionBtn>
              )}
              {isInvoiced && onOpenInvoice && (
                <ActionBtn
                  testid="estimate-detail-view-invoice"
                  primary
                  onClick={() => {
                    onClose();
                    onOpenInvoice(est.invoice_id);
                  }}
                >
                  {t("est_detail_view_invoice_btn", { n: est.invoice_number })}
                </ActionBtn>
              )}
              <ActionBtn
                testid="estimate-detail-pdf"
                onClick={downloadPdf}
              >
                {t("action_pdf")}
              </ActionBtn>
              <ActionBtn
                testid="estimate-detail-print"
                onClick={printPdf}
              >
                {t("action_print")}
              </ActionBtn>
              {emailHref && (
                <ActionBtn
                  as="a"
                  href={emailHref}
                  target="_blank"
                  rel="noreferrer"
                  testid="estimate-detail-email"
                  onClick={() => logAction("emailed", { via: "mailto" })}
                >
                  {t("action_email_icon")}
                </ActionBtn>
              )}
              {!!waDigits && (
                <ActionBtn
                  testid="estimate-detail-whatsapp"
                  onClick={openWhatsApp}
                >
                  {t("action_whatsapp")}
                </ActionBtn>
              )}
              <ActionBtn
                testid="estimate-detail-duplicate"
                onClick={() => doAction("duplicate")}
              >
                {t("action_duplicate")}
              </ActionBtn>
              {!isInvoiced && (
                <ActionBtn
                  testid="estimate-detail-follow-up"
                  onClick={() => setShowFollowUp((v) => !v)}
                >
                  {t("action_follow_up_icon")}
                </ActionBtn>
              )}
              {!isInvoiced && status !== "cancelled" && status !== "expired" && (
                <ActionBtn
                  testid="estimate-detail-cancel"
                  danger
                  onClick={() => {
                    if (window.confirm(t("est_detail_cancel_confirm"))) doAction("cancel");
                  }}
                >
                  {t("action_cancel_icon")}
                </ActionBtn>
              )}
              {isAdmin && !isInvoiced && (
                <ActionBtn
                  testid="estimate-detail-delete"
                  danger
                  onClick={softDelete}
                >
                  {t("action_delete_icon")}
                </ActionBtn>
              )}
            </div>

            {showFollowUp && (
              <div
                data-testid="estimate-detail-follow-up-form"
                className="p-4 bg-cyan-50 border-b border-[var(--border)] space-y-3"
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-1">
                    <label className="block font-mono-tech text-[0.65rem] font-bold tracking-wider text-[var(--text-secondary)] mb-1">
                      {t("est_detail_followup_date_label")}
                    </label>
                    <input
                      data-testid="estimate-detail-follow-up-date"
                      type="date"
                      value={followDate}
                      onChange={(e) => setFollowDate(e.target.value)}
                      className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block font-mono-tech text-[0.65rem] font-bold tracking-wider text-[var(--text-secondary)] mb-1">
                      {t("est_detail_followup_notes_label")}
                    </label>
                    <input
                      data-testid="estimate-detail-follow-up-notes"
                      value={followNotes}
                      onChange={(e) => setFollowNotes(e.target.value)}
                      className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <button
                  data-testid="estimate-detail-follow-up-save"
                  onClick={saveFollowUp}
                  className="font-mono-tech text-xs border border-black bg-black text-white px-4 py-2 hover:opacity-90"
                >
                  {t("save")}
                </button>
              </div>
            )}

            {/* Body */}
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="md:col-span-2 space-y-4">
                <Section title={t("est_detail_section_customer")}>
                  <div className="text-base space-y-0.5">
                    <div>
                      <b>{est.company}</b>
                    </div>
                    <div>{est.contact_person}</div>
                    <div>{est.customer_phone}</div>
                    <div>{est.customer_email}</div>
                  </div>
                </Section>

                <Section title={t("est_detail_section_meta")}>
                  <div className="text-sm grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-[var(--text-secondary)] text-xs">{t("est_detail_meta_office")} </span>
                      {est.office_name || est.office_key}
                    </div>
                    <div>
                      <span className="text-[var(--text-secondary)] text-xs">{t("est_detail_meta_sales_rep")} </span>
                      {est.sales_rep_name}
                    </div>
                    <div>
                      <span className="text-[var(--text-secondary)] text-xs">{t("est_detail_meta_created")} </span>
                      {String(est.created_at || "").slice(0, 10)}
                    </div>
                    <div>
                      <span className="text-[var(--text-secondary)] text-xs">{t("est_detail_meta_expires")} </span>
                      {String(est.expires_at || "").slice(0, 10)}
                    </div>
                    <div>
                      <span className="text-[var(--text-secondary)] text-xs">{t("est_detail_meta_delivery")} </span>
                      {est.delivery_date || "—"}
                    </div>
                    {est.follow_up_date && (
                      <div>
                        <span className="text-[var(--text-secondary)] text-xs">{t("est_detail_meta_followup")} </span>
                        {est.follow_up_date}
                      </div>
                    )}
                  </div>
                </Section>

                <Section title={t("est_detail_section_items")}>
                  <div className="border border-[var(--border)] divide-y divide-[var(--border)]">
                    {groupItemsByProduct(est.items || []).map((g) => {
                      if (g.variants.length === 1) {
                        const it = g.variants[0];
                        return (
                          <div
                            key={g.key}
                            data-testid={`estimate-detail-item-row-${it.__originalIndex}`}
                            className="p-3 flex items-center gap-3"
                          >
                            {it.catalog_image_url && (
                              <img
                                src={it.catalog_image_url}
                                alt=""
                                className="w-10 h-10 object-contain border border-[var(--border)] bg-white"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold truncate">
                                {it.description || it.product_code}
                              </div>
                              <div className="text-xs text-[var(--text-secondary)]">
                                {[it.catalog_color, it.catalog_size].filter(Boolean).join(" · ")}
                                {it.commercial_group && (
                                  <span className="ml-2 font-mono-tech">
                                    · {it.commercial_group}
                                    {it.commercial_tier ? ` @ ${it.commercial_tier}` : ""}
                                  </span>
                                )}
                              </div>
                              {it.customer_notes && (
                                <div
                                  data-testid={`estimate-detail-item-note-${it.__originalIndex}`}
                                  className="text-xs italic text-[var(--text-secondary)] mt-0.5"
                                >
                                  Nota: {it.customer_notes}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-right w-14">×{it.quantity}</div>
                            <div className="text-sm text-right w-20">{money(it.unit_price)}</div>
                            <div className="text-sm text-right w-24 font-bold">
                              {money(it.subtotal)}
                            </div>
                          </div>
                        );
                      }
                      // Grouped rendering (>=2 variants)
                      return (
                        <div
                          key={g.key}
                          data-testid={`estimate-detail-item-group-${g.key}`}
                          className="p-3"
                        >
                          <div className="flex items-center gap-3">
                            {g.header.catalog_image_url && (
                              <img
                                src={g.header.catalog_image_url}
                                alt=""
                                className="w-10 h-10 object-contain border border-[var(--border)] bg-white"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold truncate">
                                {g.header.description}
                              </div>
                              <div className="text-xs text-[var(--text-secondary)]">
                                {t("item_group_variants_count", { n: g.variants.length })} ·{" "}
                                {t("item_group_total_units", { q: g.totalQty })}
                                {g.header.commercial_group && (
                                  <span className="ml-2 font-mono-tech">
                                    · {g.header.commercial_group}
                                    {g.header.commercial_tier ? ` @ ${g.header.commercial_tier}` : ""}
                                  </span>
                                )}
                              </div>
                              {g.header.customer_notes && (
                                <div
                                  data-testid={`estimate-detail-group-note-${g.key}`}
                                  className="text-xs italic text-[var(--text-secondary)] mt-0.5"
                                >
                                  Nota: {g.header.customer_notes}
                                </div>
                              )}
                            </div>
                            <div className="text-sm text-right w-20">{money(g.header.unit_price)}</div>
                            <div className="text-sm text-right w-24 font-bold">
                              {money(g.totalAmount)}
                            </div>
                          </div>
                          <div className="mt-2 ml-13 pl-3 border-l-2 border-[var(--border)] divide-y divide-[var(--border)]">
                            {g.variants.map((it) => (
                              <div
                                key={it.__originalIndex}
                                data-testid={`estimate-detail-item-row-${it.__originalIndex}`}
                                className="py-1.5 flex items-center gap-3 text-xs text-[var(--text-secondary)]"
                              >
                                <div className="flex-1 min-w-0 truncate">
                                  {[it.catalog_color, it.catalog_size].filter(Boolean).join(" · ") || "—"}
                                </div>
                                <div className="text-right w-14">×{it.quantity}</div>
                                <div className="text-right w-24 font-bold text-[var(--text)]">
                                  {money(it.subtotal)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Section>

                {est.customer_notes && (
                  <Section title={t("est_detail_section_customer_notes")}>
                    <div className="text-sm whitespace-pre-wrap">{est.customer_notes}</div>
                  </Section>
                )}
                {est.internal_notes && (
                  <Section title={t("est_detail_section_internal_notes")}>
                    <div className="text-sm whitespace-pre-wrap bg-yellow-50 p-3 border border-yellow-200">
                      {est.internal_notes}
                    </div>
                  </Section>
                )}
              </div>

              <div className="space-y-4">
                <Section title={t("est_detail_section_summary")}>
                  <div className="bg-black text-white p-4 space-y-2 font-mono-tech">
                    <Row label={t("est_detail_summary_subtotal") || "Subtotal"} value={money(est.subtotal)} />
                    <Row
                      label={
                        est.discount_type === "percent"
                          ? t("est_detail_summary_discount_pct", { pct: est.discount_value })
                          : t("est_detail_summary_discount")
                      }
                      value={`-${money(est.discount_amount)}`}
                    />
                    <Row
                      label={
                        est.tax_exempt
                          ? t("est_detail_summary_tax_exempt")
                          : t("est_detail_summary_tax_rate", { pct: (est.sales_tax_rate * 100).toFixed(2) })
                      }
                      value={money(est.sales_tax_amount)}
                    />
                    <div className="border-t border-white/20 pt-2 flex justify-between text-xl font-bold">
                      <span>{t("est_form_summary_total")}</span>
                      <span data-testid="estimate-detail-total">{money(est.total_amount)}</span>
                    </div>
                    {/* GP 1.0 · Sprint 5 · Issue #1 — Depósito/Balance
                        removidos de la presentación del Estimate. */}
                  </div>
                </Section>

                <ClientFilesSection docType="estimates" docId={est.id} />

                <Section title={t("est_detail_section_history")}>
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {(est.history || []).slice().reverse().map((h) => (
                      <div key={h.id} className="text-xs border-l-2 border-[var(--border)] pl-2">
                        <div className="font-mono-tech font-bold uppercase">{h.action}</div>
                        <div className="text-[var(--text-secondary)]">
                          {String(h.at || "").slice(0, 16).replace("T", " ")}
                          {h.by_name ? ` · ${h.by_name}` : ""}
                        </div>
                      </div>
                    ))}
                    {(!est.history || est.history.length === 0) && (
                      <div className="text-xs text-[var(--text-secondary)]">{t("empty_no_events")}</div>
                    )}
                  </div>
                </Section>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="label-overline text-[var(--text-secondary)] mb-2">
        {title.toUpperCase()}
      </div>
      {children}
    </section>
  );
}

// Fase 1 · Archivos del cliente — el archivo vive en el perfil del cliente;
// el documento sólo guarda {file_id, note}. Quitar = sólo desvincular.
// Exportado para reutilizarlo en InvoiceDetailModal (mismos 5 archivos de fase).
export function ClientFilesSection({ docType, docId }) {
  const [files, setFiles] = useState([]);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/commercial/${docType}/${docId}/client-files`,
        { withCredentials: true }
      );
      setFiles(data);
    } catch {
      /* ignore */
    }
  }, [docType, docId]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name || file.name);
      fd.append("note", note);
      await axios.post(`${API}/commercial/${docType}/${docId}/client-files`, fd, {
        withCredentials: true,
      });
      setFile(null);
      setName("");
      setNote("");
      await load();
      toast.success("Archivo subido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir el archivo");
    } finally {
      setBusy(false);
    }
  };

  const download = async (f) => {
    try {
      const res = await axios.get(
        `${API}/commercial/${docType}/${docId}/client-files/${f.file_id}/download`,
        { responseType: "blob", withCredentials: true }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename || f.name || "archivo";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el archivo");
    }
  };

  const unlink = async (f) => {
    try {
      await axios.delete(
        `${API}/commercial/${docType}/${docId}/client-files/${f.file_id}`,
        { withCredentials: true }
      );
      await load();
      toast.success("Referencia quitada (el archivo sigue en el perfil del cliente)");
    } catch {
      toast.error("No se pudo quitar");
    }
  };

  return (
    <Section title="Archivos del cliente">
      <div data-testid="client-files-section" className="space-y-3">
        {previewFile && (
          <FilePreviewModal
            file={previewFile}
            downloadUrl={`${API}/commercial/${docType}/${docId}/client-files/${previewFile.file_id}/download`}
            onClose={() => setPreviewFile(null)}
          />
        )}
        {files.length > 0 && (
          <div className="border border-[var(--border)] divide-y divide-[var(--border)]">
            {files.map((f) => (
              <div
                key={f.file_id}
                data-testid={`client-file-row-${f.file_id}`}
                className="p-2 flex items-start gap-2 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{f.name}</div>
                  <div className="font-mono-tech text-[10px] text-[var(--text-secondary)] truncate">
                    {f.original_filename} · {Math.max(1, Math.round((f.size || 0) / 1024))} KB
                  </div>
                  {f.note && (
                    <div
                      data-testid={`client-file-note-${f.file_id}`}
                      className="text-xs mt-0.5 whitespace-pre-wrap"
                    >
                      {f.note}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  data-testid={`client-file-view-${f.file_id}`}
                  onClick={() => setPreviewFile(f)}
                  className="font-mono-tech text-[0.65rem] font-bold border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors"
                  style={{ display: isPreviewableFile(f) ? undefined : "none" }}
                >
                  Ver
                </button>
                <button
                  type="button"
                  data-testid={`client-file-download-${f.file_id}`}
                  onClick={() => download(f)}
                  className="font-mono-tech text-[0.65rem] font-bold border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors"
                >
                  Descargar
                </button>
                <button
                  type="button"
                  data-testid={`client-file-unlink-${f.file_id}`}
                  onClick={() => unlink(f)}
                  title="Quita la referencia de este documento; el archivo permanece en el perfil del cliente"
                  className="font-mono-tech text-[0.65rem] font-bold border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-2 py-1 hover:bg-[var(--alert-overdue)] hover:text-white transition-colors"
                >
                  Quitar
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="border border-dashed border-[var(--border)] p-2 space-y-2">
          <input
            key={files.length}
            type="file"
            data-testid="client-file-input"
            accept=".png,.jpg,.jpeg,.pdf,.svg,.ai,.eps"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-xs w-full"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              data-testid="client-file-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre (ej. Logo frontal)"
              className="border border-[var(--border)] px-2 py-1.5 text-xs focus:outline-none focus:border-black"
            />
            <input
              data-testid="client-file-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Nota para este trabajo (ej. Pecho izquierdo)"
              className="border border-[var(--border)] px-2 py-1.5 text-xs focus:outline-none focus:border-black"
            />
          </div>
          <button
            type="button"
            data-testid="client-file-upload"
            onClick={upload}
            disabled={!file || busy}
            className="font-mono-tech text-xs font-bold bg-black text-white border border-black px-3 py-1.5 hover:bg-[var(--primary)] hover:border-[var(--primary)] transition-colors disabled:opacity-40"
          >
            {busy ? "Subiendo…" : "Subir archivo"}
          </button>
          <div className="font-mono-tech text-[10px] text-[var(--text-secondary)]">
            PNG · JPG · PDF · SVG · AI · EPS — máx. 20 MB
          </div>
        </div>
      </div>
    </Section>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
function ActionBtn({
  children,
  testid,
  onClick,
  primary,
  danger,
  as,
  href,
  target,
  rel,
}) {
  const cls = primary
    ? "font-mono-tech text-xs font-bold bg-black text-white px-3 py-2 border border-black hover:bg-[var(--accent-magenta,#E5007D)] transition-colors"
    : danger
    ? "font-mono-tech text-xs font-bold border border-red-600 text-red-600 px-3 py-2 hover:bg-red-600 hover:text-white transition-colors"
    : "font-mono-tech text-xs font-bold border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors";
  if (as === "a") {
    return (
      <a data-testid={testid} href={href} target={target} rel={rel} onClick={onClick} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <button data-testid={testid} onClick={onClick} className={cls}>
      {children}
    </button>
  );
}
